import type {
  NbxHubOptions,
  HostMessage,
  IframeMessage,
  CloseReason,
  NbxMessage,
} from "./types";

type MessageHandler = {
  onReady?: () => void;
  onClose?: (reason: CloseReason) => void;
  onCloseCancelled?: () => void;
  onMessage?: (message: NbxMessage) => void;
  onRedirect?: (url: string) => void;
};

let messageListener: ((event: MessageEvent) => void) | null = null;

/**
 * Sends a message to the iframe
 */
export function sendMessage(
  iframe: HTMLIFrameElement,
  message: HostMessage,
): void {
  if (!iframe.contentWindow) {
    console.warn(
      "[NbxHub] Cannot send message: iframe contentWindow not available",
    );
    return;
  }

  iframe.contentWindow.postMessage(message, "*");
}

/**
 * Sends initialization config to iframe
 */
export function sendInitMessage(
  iframe: HTMLIFrameElement,
  payload: Record<string, unknown>,
): void {
  if (!iframe.contentWindow) {
    console.error(
      "[NbxHub SDK] ❌ ERROR: iframe.contentWindow no está disponible!",
    );
    return;
  }

  console.log("[NbxHub SDK] 📨 postMessage NBX_INIT enviado al iframe");
  console.log("[NbxHub SDK] 📨 iframe.contentWindow:", !!iframe.contentWindow);
  console.log("[NbxHub SDK] 📨 Payload:", JSON.stringify(payload, null, 2));

  iframe.contentWindow.postMessage(
    {
      type: "NBX_INIT",
      payload,
    },
    "*",
  );

  console.log("[NbxHub SDK] ✅ postMessage ejecutado correctamente");
}

/**
 * Sends close request to iframe
 */
export function sendCloseRequest(iframe: HTMLIFrameElement): void {
  sendMessage(iframe, {
    type: "NBX_CLOSE_REQUEST",
  });
}

/**
 * Sets up message listener for iframe messages
 */
export function setupMessageListener(
  expectedOrigin: string | null,
  handlers: MessageHandler,
): void {
  // Remove existing listener if any
  removeMessageListener();

  messageListener = (event: MessageEvent) => {
    // Origin validation (skip if expectedOrigin is null for localhost dev)
    if (expectedOrigin && event.origin !== expectedOrigin) {
      return;
    }

    const data = event.data as IframeMessage;

    if (!data || typeof data.type !== "string") {
      return;
    }

    // Only handle NBX messages
    if (!data.type.startsWith("NBX_")) {
      return;
    }

    switch (data.type) {
      case "NBX_READY":
        handlers.onReady?.();
        break;

      case "NBX_CLOSE":
        handlers.onClose?.(data.payload?.reason || "user_action");
        break;

      case "NBX_CLOSE_CANCELLED":
        handlers.onCloseCancelled?.();
        break;

      case "NBX_EVENT":
      case "NBX_NAVIGATE":
        handlers.onMessage?.({
          type: data.type,
          payload: data.payload,
        });
        break;

      case "NBX_REDIRECT":
        if (data.payload?.url) {
          handlers.onRedirect?.(data.payload.url);
        }
        break;

      default: {
        // Forward unknown NBX messages
        const unknownData = data as { type: string; payload?: unknown };
        handlers.onMessage?.({
          type: unknownData.type,
          payload: unknownData.payload,
        });
      }
    }
  };

  window.addEventListener("message", messageListener);
}

/**
 * Removes the message listener
 */
export function removeMessageListener(): void {
  if (messageListener) {
    window.removeEventListener("message", messageListener);
    messageListener = null;
  }
}

/**
 * Sends a custom message to the iframe
 */
export function postCustomMessage(
  iframe: HTMLIFrameElement,
  message: unknown,
): void {
  if (!iframe.contentWindow) {
    console.warn(
      "[NbxHub] Cannot send message: iframe contentWindow not available",
    );
    return;
  }

  iframe.contentWindow.postMessage(message, "*");
}
